/**
 * What the report's 3D figure shows, worked out from the scan document.
 *
 * The 3D view is an illustration of readings the report already prints, never
 * a measurement of its own: every bend of the figure and every marker on its
 * skin comes from an angle in the scan, graded by the same severityForAngle the
 * tables use, so the figure can never disagree with the numbers beside it.
 *
 *   - Only readings OUTSIDE their normal range bend the figure or get a marker.
 *     A normal reading leaves that joint in a neutral stance, so what stands
 *     out on the figure is exactly what the report flags.
 *   - The side of a tilt comes from the reading's stated direction ("left
 *     shoulder higher", "knee valgus"). A reading with no direction is marked
 *     but not bent, because the figure would have to guess which way.
 *   - Rearfoot: both heels are always marked (green when normal), as a foot
 *     scanner shows them; the shins only when rotated.
 *
 * Pure — no three.js — so it is tested with the rest of lib/report. The viewer
 * (lib/body3d) turns `pose` into bone rotations and `markers` into coloured
 * discs on the skin.
 */

import { rangeForAngle, severityForAngle } from '../metricBands';
import { HEEL_TEXT, SHIN_TEXT, isUnmeasured } from './scanModel';
import { ARCH_HIGH_MAX, ARCH_NORMAL_MAX, archTypeOf, shoeSizeOf, widthClassOf } from './plantarModel';

const hasDegrees = a => a && typeof a.degrees === 'number' && Number.isFinite(a.degrees);
const fmt = d => `${Math.abs(d).toFixed(1)}°`;
const RANK = { normal: 0, mild: 1, moderate: 2, severe: 3 };

/** Which patient side a direction names, or null. */
function sideIn(direction) {
  const m = /\b(left|right)\b/i.exec(direction || '');
  return m ? m[1].toLowerCase() : null;
}

/**
 * Posture markers: each anchor is one place on the body, and the readings it
 * stands for. When several readings fall on one place (forward head and the
 * craniovertebral angle are both "the neck"), the worst of them is shown.
 * `from` is the direction the marker is viewed from — the viewer centres the
 * disc where a ray from that side meets the skin.
 */
const POSTURE_ANCHORS = [
  { anchor: 'neck', from: [0.55, 0.1, 1], names: ['Forward head', 'Craniovertebral angle', 'Cervical flexion/extension'] },
  { anchor: 'head', from: [0, 0.3, 1], names: ['Head tilt', 'Head center deviation'] },
  { anchor: 'shoulder', sided: true, from: [0.5, 0.7, 0.6], names: ['Shoulder tilt'] },
  { anchor: 'leftShoulder', from: [0.9, 0.3, 0.6], names: ['Forward shoulder'] },
  { anchor: 'upperBack', from: [0, 0.1, -1], names: ['Spine deviation', 'Trunk lean', 'Shoulder-to-pelvis relationship'] },
  { anchor: 'hip', sided: true, from: [1, 0.1, 0.45], names: ['Pelvic obliquity', 'Pelvic shift'] },
  { anchor: 'leftHipSide', from: [1, 0, 0.2], names: ['Hip position', 'Pelvic-to-trunk relationship'] },
  { anchor: 'leftKnee', from: [-0.2, 0, 1], names: ['Left knee deviation', 'Left knee flexion'] },
  { anchor: 'rightKnee', from: [0.2, 0, 1], names: ['Right knee deviation', 'Right knee flexion'] },
  { anchor: 'leftShin', from: [1, 0, 0.6], names: ['Left leg tilt'] },
  { anchor: 'rightShin', from: [-1, 0, 0.6], names: ['Right leg tilt'] },
];

/** Bone rotations for an out-of-range reading: [bone, axis, degrees], world axes, patient faces +z, left is +x. */
function poseFor(a) {
  const d = Math.abs(a.degrees);
  const dir = a.direction || '';
  const side = sideIn(dir);
  switch (a.name) {
    case 'Forward head':
      return /behind/i.test(dir) ? [] : [['Neck', 'x', d * 0.85], ['Head', 'x', -d * 0.35]];
    case 'Shoulder tilt':
      if (side === 'left') return [['LeftShoulder', 'z', d * 1.4]];
      if (side === 'right') return [['RightShoulder', 'z', -d * 1.4]];
      return [];
    case 'Pelvic obliquity': {
      if (!side) return [];
      const s = side === 'left' ? 1 : -1;
      // Tilt the pelvis, keep the trunk and both legs upright above and below it.
      return [['Hips', 'z', s * d], ['Spine', 'z', -s * d], ['LeftUpLeg', 'z', -s * d], ['RightUpLeg', 'z', -s * d]];
    }
    case 'Spine deviation':
      // "trunk leans to the left": the top of the trunk moves towards +x, a -z turn.
      if (side === 'left') return [['Spine', 'z', -d]];
      if (side === 'right') return [['Spine', 'z', d]];
      return [];
    case 'Trunk lean':
      return [['Spine', 'x', /backward/i.test(dir) ? -d : d]];
    case 'Head tilt':
      // "head tilted to the left": the head rolls towards the patient's left (+x).
      if (side === 'left') return [['Head', 'z', -d]];
      if (side === 'right') return [['Head', 'z', d]];
      return [];
    case 'Left knee deviation':
    case 'Right knee deviation': {
      const valgus = /valgus/i.test(dir), varus = /varus/i.test(dir);
      if (!valgus && !varus) return [];
      const left = a.name.startsWith('Left');
      // Valgus: the knee drops towards the midline and the shin angles back out.
      const s = (left ? 1 : -1) * (valgus ? 1 : -1);
      const leg = left ? 'Left' : 'Right';
      return [[`${leg}UpLeg`, 'z', -s * d / 2], [`${leg}Leg`, 'z', s * d]];
    }
    default:
      return [];
  }
}

function worst(readings) {
  return readings.reduce((w, r) => (!w || RANK[r.severity] > RANK[w.severity] ? r : w), null);
}

/** The posture figure: pose and markers from the posture views' angles. */
export function postureFigure(views) {
  const angles = (views ?? [])
    .filter(v => !isUnmeasured(v))
    .flatMap(v => v.angles ?? [])
    .filter(hasDegrees)
    .map(a => ({ ...a, severity: severityForAngle(a) }));
  if (!angles.length) return null;
  const flagged = angles.filter(a => a.severity !== 'normal');

  const pose = flagged.flatMap(poseFor);
  const markers = [];
  for (const spec of POSTURE_ANCHORS) {
    const r = worst(flagged.filter(a => spec.names.includes(a.name)));
    if (!r) continue;
    let anchor = spec.anchor, from = spec.from;
    if (spec.sided) {
      const side = sideIn(r.direction) ?? 'left';
      anchor = `${side}${spec.anchor[0].toUpperCase()}${spec.anchor.slice(1)}`;
      if (side === 'right') from = [-from[0], from[1], from[2]];
    }
    markers.push({
      id: `${anchor}:${r.name}`,
      label: r.name,
      value: fmt(r.degrees),
      direction: r.direction ?? null,
      severity: r.severity,
      anchor,
      from,
    });
  }
  return { pose, markers };
}

const HEEL_SIGN = { valgus: 1, varus: -1 };
const SHIN_SIGN = { 'internal-rotation': -1, 'external-rotation': 1 };

/**
 * The rearfoot figure: the heels tilted by the measured calcaneal angles, the
 * shins turned by the measured rotation, and a marker on each heel and on any
 * rotated shin. From the standing leg capture's findings.
 */
export function rearfootFigure(poses) {
  const stand = (poses ?? []).find(p => p.findings && !isUnmeasured(p)) ?? null;
  const f = stand?.findings;
  if (!f) return null;
  const pose = [], markers = [];
  const heel = (side, finding) => {
    if (!hasDegrees(finding)) return;
    const name = `${side} calcaneal angle`;
    const severity = severityForAngle({ name, degrees: finding.degrees });
    const s = HEEL_SIGN[finding.finding] ?? 0;
    // +z rotation tips the heel's underside towards +x (the patient's left).
    // Eversion (valgus) tips it outward: +x on the left foot, -x on the right.
    if (s) pose.push([`${side}Foot`, 'z', s * (side === 'Left' ? 1 : -1) * Math.abs(finding.degrees)]);
    markers.push({
      id: `heel${side}`, label: `${side} heel`, value: fmt(finding.degrees),
      direction: finding.finding && finding.finding !== 'normal' ? finding.finding : null,
      severity, anchor: `heel${side}`, from: [0, -0.35, -1],
    });
  };
  const shin = (side, finding) => {
    if (!hasDegrees(finding)) return;
    const name = `${side} shin rotation`;
    const severity = severityForAngle({ name, degrees: finding.degrees });
    const s = SHIN_SIGN[finding.finding] ?? 0;
    // Internal rotation turns the toes towards the midline: -y on the left leg, +y on the right.
    if (s) pose.push([`${side}Leg`, 'y', s * (side === 'Left' ? 1 : -1) * Math.abs(finding.degrees)]);
    if (severity === 'normal') return;
    markers.push({
      id: `shin${side}`, label: `${side} shin`, value: fmt(finding.degrees),
      direction: finding.finding && finding.finding !== 'neutral' ? finding.finding.replace('-', ' ') : null,
      severity, anchor: `shin${side}`, from: [side === 'Left' ? -0.4 : 0.4, 0, -1],
    });
  };
  heel('Left', f.heelLeft); heel('Right', f.heelRight);
  shin('Left', f.shinLeft); shin('Right', f.shinRight);
  if (!markers.length && !pose.length) return null;
  return { pose, markers };
}

/** Camera preset for a posture view: front, side (from the patient's left) or back. */
export function cameraForView(view) {
  if (view === 'side' || view === 'left' || view === 'right') return view === 'right' ? 'right' : 'side';
  if (view === 'back') return 'back';
  return 'front';
}

// ── The 3D findings drawer ─────────────────────────────────────────────────
// Everything the drawer lists, tab by tab, with where each item sits on the
// figure. Each item: { id, name, meta, value, ref, severity, anchor, from }.
// `severity` is the report's own grade; plantar readings that have no normal
// range are 'info'.

const cap = s => (s ? s[0].toUpperCase() + s.slice(1) : s);
const refText = a => {
  const r = rangeForAngle(a);
  return r ? `normal ${r[0]}–${r[1]}°` : '';
};

/** Where a posture reading sits on the body, from POSTURE_ANCHORS. */
function anchorOf(a) {
  const spec = POSTURE_ANCHORS.find(s => s.names.includes(a.name));
  if (!spec) return { anchor: 'upperBack', from: [0, 0.1, -1] };
  if (!spec.sided) return { anchor: spec.anchor, from: spec.from };
  const side = sideIn(a.direction) ?? 'left';
  return {
    anchor: `${side}${spec.anchor[0].toUpperCase()}${spec.anchor.slice(1)}`,
    from: side === 'right' ? [-spec.from[0], spec.from[1], spec.from[2]] : spec.from,
  };
}

function postureItems(views) {
  const items = [];
  let normal = 0;
  for (const v of (views ?? []).filter(x => !isUnmeasured(x))) {
    for (const a of (v.angles ?? []).filter(hasDegrees)) {
      const severity = severityForAngle(a);
      if (severity === 'normal') { normal++; continue; }
      items.push({
        id: `p:${v.view ?? ''}:${a.name}`,
        name: a.name,
        meta: a.direction ? cap(a.direction) : (v.label ?? ''),
        value: fmt(a.degrees),
        ref: refText(a),
        severity,
        ...anchorOf(a),
      });
    }
  }
  items.sort((x, y) => RANK[y.severity] - RANK[x.severity]);
  return { items, normal };
}

function legItems(poses) {
  const stand = (poses ?? []).find(p => p.findings && !isUnmeasured(p)) ?? null;
  const f = stand?.findings ?? {};
  const items = [];
  for (const side of ['Left', 'Right']) {
    const h = f[`heel${side}`];
    if (hasDegrees(h)) {
      const name = `${side} calcaneal angle`;
      items.push({
        id: `heel${side}`, name: `${side} heel`, value: fmt(h.degrees),
        meta: `Calcaneal angle — ${HEEL_TEXT[h.finding] ?? h.finding ?? 'measured'}`,
        ref: refText({ name }), severity: severityForAngle({ name, degrees: h.degrees }),
        anchor: `heel${side}`, from: [0, -0.35, -1],
      });
    }
  }
  for (const side of ['Left', 'Right']) {
    const s = f[`shin${side}`];
    if (hasDegrees(s)) {
      const name = `${side} shin rotation`;
      items.push({
        id: `shin${side}`, name: `${side} shin`, value: fmt(s.degrees),
        meta: `Malleolar line — ${SHIN_TEXT[s.finding] ?? s.finding ?? 'measured'}`,
        ref: refText({ name }), severity: severityForAngle({ name, degrees: s.degrees }),
        anchor: `shin${side}`, from: [side === 'Left' ? -0.4 : 0.4, 0, -1],
      });
    }
  }
  const sym = (stand?.angles ?? []).find(a => a.name === 'Heel symmetry' && hasDegrees(a));
  if (sym) {
    items.push({
      id: 'heelSym', name: 'Heel height', value: fmt(sym.degrees),
      meta: sym.direction ? cap(sym.direction) : 'Level of the two heels',
      ref: refText(sym), severity: severityForAngle(sym), anchor: 'heelsBetween', from: [0, 0, -1],
    });
  }
  items.sort((x, y) => RANK[y.severity] - RANK[x.severity]);
  return items;
}

function loadShareSeverity(bilateral) {
  const off = Math.max(...['left', 'right'].map(k => (typeof bilateral?.[k] === 'number' ? Math.abs(bilateral[k] - 50) : 0)));
  if (off <= 5) return 'normal';
  if (off <= 10) return 'mild';
  if (off <= 15) return 'moderate';
  return 'severe';
}

function archSeverity(index) {
  if (index >= ARCH_HIGH_MAX && index <= ARCH_NORMAL_MAX) return 'normal';
  const off = index < ARCH_HIGH_MAX ? ARCH_HIGH_MAX - index : index - ARCH_NORMAL_MAX;
  return off <= 0.04 ? 'mild' : off <= 0.08 ? 'moderate' : 'severe';
}

function plantarItems(a) {
  if (!a) return [];
  const items = [];
  const b = a.bilateral;
  if (typeof b?.left === 'number' && typeof b?.right === 'number') {
    const heavier = b.left > b.right ? 'left' : b.right > b.left ? 'right' : null;
    items.push({
      id: 'loadShare', name: 'Load share', value: `${Math.round(b.left)} / ${Math.round(b.right)} %`,
      meta: heavier ? `More weight on the ${heavier} foot` : 'Weight evenly shared', ref: 'left / right, 50 ± 5 %',
      severity: loadShareSeverity(b), anchor: heavier ? `cop${cap(heavier)}` : 'copLeft',
    });
  }
  const feet = a.feet ?? {};
  for (const side of ['Left', 'Right']) {
    const foot = feet[side.toLowerCase()];
    if (!foot) continue;
    if (typeof foot.archIndex === 'number' && foot.archIndex > 0) {
      items.push({
        id: `arch${side}`, name: `${side} arch index`, value: foot.archIndex.toFixed(2),
        meta: archTypeOf(foot.archIndex) ?? '', ref: `normal ${ARCH_HIGH_MAX}–${ARCH_NORMAL_MAX}`,
        severity: archSeverity(foot.archIndex), anchor: `arch${side}`,
      });
    }
    if (typeof foot.peakPressureKpa === 'number') {
      items.push({
        id: `peak${side}`, name: `${side} peak pressure`, value: `${Math.round(foot.peakPressureKpa)} kPa`,
        meta: typeof foot.meanPressureKpa === 'number' ? `Mean ${foot.meanPressureKpa.toFixed(1)} kPa` : '',
        ref: 'highest point under the foot', severity: 'info', anchor: `peak${side}`,
      });
    }
    if (typeof foot.forefootPct === 'number') {
      items.push({
        id: `fore${side}`, name: `${side} forefoot load`, value: `${Math.round(foot.forefootPct)} %`,
        meta: `Hindfoot ${Math.round(100 - foot.forefootPct)} %`, ref: "share of this foot's load",
        severity: 'info', anchor: `forefoot${side}`,
      });
    }
  }
  // Scans from before per-foot readings: the whole-mat figures, once.
  if (!feet.left && !feet.right) {
    if (typeof a.archIndex === 'number' && a.archIndex > 0) {
      items.push({
        id: 'arch', name: 'Arch index (both feet)', value: a.archIndex.toFixed(2), meta: archTypeOf(a.archIndex) ?? '',
        ref: `normal ${ARCH_HIGH_MAX}–${ARCH_NORMAL_MAX}`, severity: archSeverity(a.archIndex), anchor: 'archLeft',
      });
    }
    if (typeof a.peakPressureKpa === 'number') {
      items.push({
        id: 'peak', name: 'Peak pressure (both feet)', value: `${Math.round(a.peakPressureKpa)} kPa`, meta: '',
        ref: 'highest point on the mat', severity: 'info', anchor: 'peakAny',
      });
    }
  }
  return items;
}

/**
 * The plantar readings of a camera-only scan: the optical vitals of the
 * plantar photo, and each foot's area on the glass. Estimates from the
 * photograph, not measurements, and said so.
 */
function cameraPlantarItems(archVitals, footSize) {
  const items = [];
  const v = Array.isArray(archVitals) ? archVitals[0] : null;
  if (v && v.contactFound !== false) {
    const pct = (id, name, value, meta, anchor) => {
      if (typeof value === 'number') items.push({ id, name, value: `${Math.round(value)} %`, meta, ref: 'optical estimate', severity: 'info', anchor });
    };
    pct('medialBias', 'Medial bias', v.medialBias, 'Load towards the inner side of the feet', 'archLeft');
    pct('highPressure', 'High-contact area', v.highPressure, 'Share of the soles pressing hardest', 'peakAny');
    pct('load', 'Optical load', v.load, 'How much of the soles bears on the glass', 'copLeft');
  }
  for (const side of ['Left', 'Right']) {
    const a = footSize?.[side.toLowerCase()]?.areaCm2;
    if (typeof a === 'number' && a > 0) {
      items.push({ id: `area${side}`, name: `${side} contact area`, value: `${a.toFixed(1)} cm²`, meta: 'Sole on the podoscope glass', ref: 'camera', severity: 'info', anchor: `cop${side}` });
    }
  }
  return items;
}

/**
 * The plantar map images to build the 3D plate from: the whole-mat sensor
 * frame when there is one, else the two single-foot frames side by side;
 * with no mat, the podoscope camera's plantar map (an optical estimate).
 */
function plantarMaps(maps) {
  const sensor = (maps ?? []).filter(m => m && (m.url || m.dataUrl) && m.source !== 'camera');
  const url = m => m.url || m.dataUrl;
  if (!sensor.length) {
    const camera = (maps ?? []).filter(m => m && (m.url || m.dataUrl) && m.source === 'camera');
    const plantar = camera.find(m => /plantar/i.test(m.label ?? '')) ?? camera[0];
    return plantar ? [{ url: url(plantar), place: 'full', source: 'camera' }] : [];
  }
  const full = sensor.find(m => /both feet|full/i.test(m.label ?? ''));
  if (full) return [{ url: url(full), place: 'full' }];
  const left = sensor.find(m => /left/i.test(m.label ?? ''));
  const right = sensor.find(m => /right/i.test(m.label ?? ''));
  if (left || right) return [left && { url: url(left), place: 'left' }, right && { url: url(right), place: 'right' }].filter(Boolean);
  return [];
}

/** The plantar pressure tab: the mat's readings, or with no mat the camera's estimates. */
function plantarSection(analytics, maps, archVitals, footSize) {
  const plates = plantarMaps(maps);
  if (analytics) return { items: plantarItems(analytics), maps: plates, measured: true, source: 'mat' };
  const items = cameraPlantarItems(archVitals, footSize);
  return { items, maps: plates, measured: items.length > 0 || plates.length > 0, source: plates.length || items.length ? 'camera' : null };
}

// ── Foot measurements (the MyFootBalance-style panel) ─────────────────────
// Per foot: length, width (narrow / standard / wide), arch height and foot
// alignment up top; ball width, heel width, foot area and a shoe-size guide
// below. Lengths and widths are the footprint's, from the pressure mat; the
// arch from its arch index; the alignment from the standing heel angle.

const ARCH_KIND = { 'High arch': 'high', Normal: 'normal', 'Low / flat': 'low' };
const ARCH_TEXT = { high: 'High arch', normal: 'Normal arch', low: 'Low arch' };
const ALIGN = {
  valgus: { kind: 'over', text: 'Overpronation' },
  varus: { kind: 'supination', text: 'Supination' },
  normal: { kind: 'neutral', text: 'Neutral' },
};
const mmText = v => (typeof v === 'number' && v > 0 ? String(Math.round(v)) : null);

/**
 * The foot measurements panel: `rows` up top, `detailed` below, each row with
 * a left and a right cell; `feet` carries the numbers the 3D feet are sized by.
 * Each row's `view` says which 3D view its (+) opens.
 *
 * Sizes come from the mat's footprint when it measured them, otherwise from
 * the calibrated podoscope camera (scan.footSize); `source` says which.
 */
export function footMeasurements(analytics, poses, footSize = null) {
  const mat = analytics?.feet ?? {};
  const matSized = Boolean(mat.left?.footLengthMm || mat.right?.footLengthMm);
  const camSized = !matSized && Boolean(footSize?.left?.footLengthMm || footSize?.right?.footLengthMm);
  // Widths only from a foot's outline: from its lit contact they read under the foot.
  const fromCamera = f => (f?.footLengthMm ? {
    footLengthMm: f.footLengthMm,
    ...(f.measuredFrom === 'outline' ? { footWidthMm: f.footWidthMm, ballWidthMm: f.ballWidthMm, heelWidthMm: f.heelWidthMm } : {}),
    footAreaCm2: f.areaCm2, measuredFrom: f.measuredFrom,
  } : null);
  const feet = camSized
    ? { left: { ...(mat.left ?? {}), ...fromCamera(footSize.left) }, right: { ...(mat.right ?? {}), ...fromCamera(footSize.right) } }
    : mat;
  const stand = (poses ?? []).find(p => p.findings && !isUnmeasured(p)) ?? null;
  const heel = side => stand?.findings?.[`heel${side}`] ?? null;
  const per = fn => ({ left: fn(feet.left ?? null, 'Left'), right: fn(feet.right ?? null, 'Right') });
  const has = row => Boolean(row.left || row.right);

  const length = { id: 'length', label: 'Foot length', view: 'top',
    ...per(f => (mmText(f?.footLengthMm) ? { value: mmText(f.footLengthMm), unit: 'mm' } : null)) };
  const width = { id: 'width', label: 'Foot width', view: 'top',
    ...per(f => {
      const w = mmText(f?.footWidthMm);
      if (!w) return null;
      // The camera's widths are the foot's own only from its AI outline; from
      // the lit contact they read narrower than the foot: no class from those.
      const c = camSized && f.measuredFrom !== 'outline' ? null : widthClassOf(f.footWidthMm, f.footLengthMm);
      return c ? { title: c.toUpperCase(), value: w, unit: 'mm' } : { value: w, unit: 'mm' };
    }) };
  const arch = { id: 'arch', label: 'Arch height', view: 'arch',
    ...per(f => {
      const kind = ARCH_KIND[archTypeOf(f?.archIndex)];
      return kind ? { icon: `arch-${kind}`, text: ARCH_TEXT[kind], note: `arch index ${f.archIndex.toFixed(2)}`, kind } : null;
    }) };
  const align = { id: 'align', label: 'Foot alignment', view: 'back',
    ...per((_, side) => {
      const h = heel(side);
      const a = h && ALIGN[h.finding];
      return a ? { icon: `align-${a.kind}`, text: a.text, note: typeof h.degrees === 'number' ? `heel ${Math.abs(h.degrees).toFixed(1)}°` : '', kind: a.kind } : null;
    }) };

  const detailed = [
    { id: 'ball', label: 'Ball width', view: 'top', ...per(f => (mmText(f?.ballWidthMm) ? { value: mmText(f.ballWidthMm), unit: 'mm' } : null)) },
    { id: 'heel', label: 'Heel width', view: 'top', ...per(f => (mmText(f?.heelWidthMm) ? { value: mmText(f.heelWidthMm), unit: 'mm' } : null)) },
    { id: 'area', label: 'Contact area', view: 'top', ...per(f => (mmText(f?.footAreaCm2) ? { value: mmText(f.footAreaCm2), unit: 'cm²' } : null)) },
    { id: 'size', label: 'Shoe size (guide)', view: 'top', ...per(f => {
      const s = shoeSizeOf(f?.footLengthMm);
      return s ? { value: s.replace(' / ', ' · '), unit: '' } : null;
    }) },
  ].filter(has);
  const rows = [length, width, arch, align].filter(has);

  const dims = side => {
    const f = feet[side];
    return f && f.footLengthMm ? { lengthMm: f.footLengthMm, widthMm: f.footWidthMm ?? null } : null;
  };
  return {
    measured: rows.length + detailed.length > 0,
    source: matSized ? 'mat' : camSized ? 'camera' : null,
    // The camera sizes are from each foot's AI outline, not only its contact.
    outlined: camSized && [footSize.left, footSize.right].some(f => f?.measuredFrom === 'outline'),
    rows, detailed,
    feet: { left: dims('left'), right: dims('right') },
  };
}

/**
 * Everything the 3D findings drawer shows, from the scan document and the
 * views/poses/maps the report already derived from it.
 */
export function findings3d({ views, poses, maps, analytics, chain, footSize = null, archVitals = null }) {
  const posture = postureFigure(views);
  const rear = rearfootFigure(poses);
  const p = postureItems(views);
  return {
    // One patient: the posture readings and the rearfoot readings bend the same body.
    pose: [...(posture?.pose ?? []), ...(rear?.pose ?? [])],
    posture: { items: p.items, normalCount: p.normal, measured: Boolean(posture) },
    legs: { items: legItems(poses), measured: Boolean(rear) },
    feet: plantarSection(analytics, maps, archVitals, footSize),
    measure: footMeasurements(analytics, poses, footSize),
    chain: typeof chain === 'string' && chain.trim() ? chain.trim() : null,
  };
}
